/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { CardLinkType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface CardLinkProps {
  id: string;
  type: CardLinkType;
  fromCardId: string;
  toCardId: string;
  toCardNumber: string;
  rendezvousRequestId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class CardLinkRecord extends Record<CardLinkProps>({
  id: null as any,
  type: null as any,
  fromCardId: null as any,
  toCardId: null as any,
  toCardNumber: null as any,
  rendezvousRequestId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    type: CardLinkType;
    fromCardId: string;
    toCardId: string;
    toCardNumber: string;
    rendezvousRequestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): CardLinkRecord {
    if (json == null) {
      throw new Error('CardLinkRecord.fromJS: parameter cannot be null');
    }
    return new CardLinkRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'CardLinkJson.id',
      ),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', CardLinkType.SupportLeg),
        'CardLinkJson.type',
      ),
      fromCardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'fromCardId', '00000000-0000-0000-0000-000000000000'),
        'CardLinkJson.fromCardId',
      ),
      toCardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'toCardId', '00000000-0000-0000-0000-000000000000'),
        'CardLinkJson.toCardId',
      ),
      toCardNumber: getFromOrThrow(json, (j) => j.toCardNumber, 'CardLinkJson.toCardNumber'),
      rendezvousRequestId: json.rendezvousRequestId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'CardLinkJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    type: CardLinkType;
    fromCardId: string;
    toCardId: string;
    toCardNumber: string;
    rendezvousRequestId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [CardLinkRecord, Error[]] {
    if (json == null) {
      throw new Error('CardLinkRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new CardLinkRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'CardLinkJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', CardLinkType.SupportLeg),
            'CardLinkJson.type',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromCardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'fromCardId', '00000000-0000-0000-0000-000000000000'),
            'CardLinkJson.fromCardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      toCardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'toCardId', '00000000-0000-0000-0000-000000000000'),
            'CardLinkJson.toCardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      toCardNumber: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.toCardNumber, 'CardLinkJson.toCardNumber'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rendezvousRequestId: tryOrDefault(
        () => json.rendezvousRequestId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'CardLinkJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: CardLinkProps) {
    super(props);
    if (props == null) {
      throw new Error('CardLinkRecord constructor parameter cannot be null');
    }
  }
}

export abstract class CardLinkViewModelBase implements CardLinkProps {
  protected readonly _props: CardLinkProps;
  private _json?: CardLinkProps;

  get id(): string {
    return this._props.id;
  }
  get type(): CardLinkType {
    return this._props.type;
  }
  get fromCardId(): string {
    return this._props.fromCardId;
  }
  get toCardId(): string {
    return this._props.toCardId;
  }
  get toCardNumber(): string {
    return this._props.toCardNumber;
  }
  get rendezvousRequestId(): string | null {
    return this._props.rendezvousRequestId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isReferral() {
    return this._props.type === CardLinkType.Referral;
  }
  public get isSupportLeg() {
    return this._props.type === CardLinkType.SupportLeg;
  }

  public isAnyType(...cardLinkType: CardLinkType[]) {
    return cardLinkType.includes(this._props.type);
  }

  constructor(props: CardLinkProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        type: this._props.type,
        fromCardId: this._props.fromCardId,
        toCardId: this._props.toCardId,
        toCardNumber: this._props.toCardNumber,
        rendezvousRequestId: this._props.rendezvousRequestId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllCardLinkEntities = (state: AppState) => state.entityStores.cardLink.entities;

export const selectCardLinkById = (id: string) => (state: AppState) =>
  state.entityStores.cardLink.entities.get(id);

export const selectCardLinkMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.cardLink.meta.get(id);

export const selectCardLinkAndMetadataById = (id: string) =>
  createSelector(selectCardLinkById(id), selectCardLinkMetadataById(id), (cardLink, meta) => ({
    cardLink,
    meta,
  }));

export function selectCardLinkEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllCardLinkEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, CardLinkRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectCardLinkEntitiesByType(type: CardLinkType) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.type === type).valueSeq(),
  );
}

export function selectCardLinkEntitiesByFromCardId(fromCardId: string) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.fromCardId === fromCardId).valueSeq(),
  );
}

export function selectCardLinkEntitiesByToCardId(toCardId: string) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.toCardId === toCardId).valueSeq(),
  );
}

export function selectCardLinkEntitiesByToCardNumber(toCardNumber: string) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.toCardNumber === toCardNumber).valueSeq(),
  );
}

export function selectCardLinkEntitiesByRendezvousRequestId(rendezvousRequestId: string) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.rendezvousRequestId === rendezvousRequestId).valueSeq(),
  );
}

export function selectCardLinkEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllCardLinkEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
